import { useQuery } from '@tanstack/react-query';
import { Link, useParams } from 'react-router';
import { AlertTriangleIcon, UserIcon, XCircleIcon } from 'lucide-react';

import { api, type DossierModule } from '@/lib/api';
import { useContent, useDateTime, useLabel, usePercent, useT } from '@/lib/i18n';
import { PageHeader } from '@/components/page-header';
import { EmptyState } from '@/components/empty-state';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
import { Skeleton } from '@/components/ui/skeleton';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { useModuleName } from '@/hooks/use-module-name';

/**
 * One candidate — the person-centred view (`plan-preproduction` W1).
 *
 * ### The screen every other screen was not
 *
 * The console answers questions about *modules*: what is in one, how well it is
 * covered, how its results average. That is the right unit for authoring and the
 * wrong one for delivery — an operator asked "how is NF-2026-01761 doing?" had
 * to read the Candidates card, then Results filtered by reference, then hold the
 * persona's plan in their head to work out what was missing. This is that,
 * assembled.
 *
 * ### It draws the PLAN, and that is the whole difference
 *
 * Every row of the journey below is a module the candidate was *pinned to sit*,
 * whether or not they have started it. A screen built from the instance feed can
 * only draw what exists, so a candidate who has finished one of three modules
 * looks finished. `pending` rows are the ones that make this answer "what is
 * left" instead of "what happened".
 *
 * ### No scores here, deliberately
 *
 * The dossier carries result ids and no score blocks. A journey view's job is to
 * say a result exists and get you to it in one click; the numbers, the bands and
 * the QC hold all belong to the result screen, which already handles them. Two
 * screens rendering scores would be two screens that have to agree about a hold.
 */

const STATE_TONE: Record<string, 'default' | 'secondary' | 'destructive' | 'outline'> = {
  completed: 'default',
  in_progress: 'secondary',
  pending: 'outline',
  submitted: 'secondary',
  scoring: 'secondary',
  expired: 'destructive',
  flagged: 'destructive',
};

/** One module of the plan. */
function ModuleRow({ module: m, flags }: { module: DossierModule; flags: string[] }) {
  const t = useT();
  const label = useLabel();
  const moduleName = useModuleName();
  const dateTime = useDateTime();
  const percent = usePercent();

  const started = m.state !== 'pending';

  return (
    <div className="flex flex-wrap items-center gap-x-3 gap-y-2 border-t py-3 first:border-t-0">
      <div className="min-w-48 flex-1">
        <p className="text-sm font-medium">{moduleName(m.moduleId)}</p>
        {m.completedAt && (
          <p className="text-muted-foreground text-xs">{dateTime(m.completedAt)}</p>
        )}
      </div>

      <Badge variant={STATE_TONE[m.state] ?? 'outline'}>{label('state', m.state)}</Badge>

      {/* A bar only where there is progress to describe. A 0% bar on a module
          nobody has opened reads as a stalled run rather than an unstarted one. */}
      {started && typeof m.progress === 'number' && (
        <div className="flex w-32 items-center gap-2">
          <Progress value={m.progress} className="h-1.5" />
          <span className="text-muted-foreground text-xs tabular-nums">{percent(m.progress)}</span>
        </div>
      )}

      {flags.map((flag) => (
        <Badge key={flag} variant="destructive">{label('qcFlag', flag)}</Badge>
      ))}

      <div className="ms-auto flex items-center gap-2">
        {m.resultId ? (
          <Button variant="outline" size="sm" asChild>
            <Link to={`/results/${m.resultId}`}>{t('candidates.viewResult')}</Link>
          </Button>
        ) : m.assessmentId ? (
          // An open run is resumable — this is the harness, and picking a
          // stalled candidate back up is the reason to notice they are stalled.
          <Button variant="outline" size="sm" asChild>
            <Link to={`/run/${m.assessmentId}`}>{t('candidate.resume')}</Link>
          </Button>
        ) : (
          <span className="text-muted-foreground text-xs">{t('candidate.notStarted')}</span>
        )}
      </div>
    </div>
  );
}

export function CandidateDetail() {
  const { candidateRef = '' } = useParams();
  const t = useT();
  const content = useContent();
  const dateTime = useDateTime();
  const percent = usePercent();

  const { data, isLoading, error } = useQuery({
    queryKey: ['candidate', candidateRef],
    queryFn: () => api.candidate(candidateRef),
    enabled: Boolean(candidateRef),
    // A journey moves while someone is watching it — the same cadence the
    // Candidates list refreshes at.
    refetchInterval: 30_000,
  });

  if (isLoading) {
    return (
      <div className="mx-auto max-w-4xl space-y-4">
        <Skeleton className="h-24 w-full" />
        <Skeleton className="h-64 w-full" />
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="mx-auto max-w-4xl">
        <EmptyState
          icon={XCircleIcon}
          title={t('candidate.notFound')}
          description={error instanceof Error ? error.message : t('table.errorDescription')}
          actions={
            <Button variant="outline" asChild>
              <Link to="/candidates">{t('candidate.backToList')}</Link>
            </Button>
          }
        />
      </div>
    );
  }

  // Flags belong to the LATEST result for a module, which is the one the row
  // links to. Older results stay visible in the lineage section below.
  const flagsByModule = new Map<string, string[]>();
  for (const r of data.results) {
    if (!flagsByModule.has(r.moduleId)) flagsByModule.set(r.moduleId, r.qcFlags);
  }

  const superseded = data.results.filter((r) => r.supersedes !== null);

  return (
    <div className="mx-auto max-w-4xl">
      <PageHeader
        eyebrow={t('nav.candidates')}
        // The opaque reference IS the person, as far as this platform is
        // concerned. Never a name — there is no name to show.
        title={data.candidateRef}
        description={
          data.personaName
            ? `${content(data.personaName)} · ${data.planVersion}`
            : (data.planVersion ?? '')
        }
        actions={
          <Badge variant={data.overall.percentComplete === 100 ? 'default' : 'secondary'}>
            {t('candidate.completeOf', {
              completed: data.overall.completed,
              total: data.modules.length,
            })}
          </Badge>
        }
      />

      {/**
        * Two facts an operator needs before they trust anything below, and both
        * are quiet failures otherwise.
        */}
      {data.resolvedFrom === 'runs' && (
        <div className="bg-muted/50 mb-4 rounded-md border p-3 text-sm" role="status">
          {/* Not an error: it is how every harness candidate exists. But an
              integration problem looks exactly like this, so it is stated. */}
          {t('candidate.unregistered')}
        </div>
      )}

      {!data.planResolvable && (
        <div
          className="border-destructive/40 bg-destructive/5 mb-4 flex items-start gap-2 rounded-md border p-3"
          role="alert"
        >
          <AlertTriangleIcon className="text-destructive mt-0.5 size-4 shrink-0" />
          <p className="text-sm">{t('candidate.planUnresolvable', { version: data.planVersion ?? '' })}</p>
        </div>
      )}

      <Card className="mb-4">
        <CardHeader>
          <CardTitle className="text-base">{t('candidate.journey')}</CardTitle>
          <CardDescription>{t('candidate.journey.hint')}</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="mb-4 flex items-center gap-3">
            <Progress value={data.overall.percentComplete} className="h-2" />
            <span className="text-muted-foreground shrink-0 text-sm tabular-nums">
              {percent(data.overall.percentComplete)}
            </span>
          </div>

          {data.modules.length === 0 ? (
            <EmptyState icon={UserIcon} title={t('candidate.noPlan')} description={t('candidate.noPlan.hint')} />
          ) : (
            data.modules.map((m) => (
              <ModuleRow key={m.moduleId} module={m} flags={flagsByModule.get(m.moduleId) ?? []} />
            ))
          )}
        </CardContent>
      </Card>

      {/* Only when there IS lineage. Results are append-only, so a re-scored
          candidate has several per module — without this the journey above looks
          like it lost one. A candidate scored once has nothing to explain. */}
      {superseded.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="text-base">{t('candidate.lineage')}</CardTitle>
            <CardDescription>{t('candidate.lineage.hint')}</CardDescription>
          </CardHeader>
          <CardContent className="space-y-2">
            {data.results.map((r) => (
              <div key={r.resultId} className="flex flex-wrap items-center gap-x-3 gap-y-1 border-t pt-2 text-sm first:border-t-0 first:pt-0">
                <Link to={`/results/${r.resultId}`} className="text-primary font-mono text-xs underline underline-offset-4" dir="ltr">
                  {r.resultId}
                </Link>
                <span className="text-muted-foreground text-xs">
                  {t('modules.configVersion', { version: r.configVersion })}
                </span>
                {r.supersedes ? (
                  <span className="text-muted-foreground font-mono text-xs" dir="ltr">
                    ↳ {r.supersedes}
                  </span>
                ) : (
                  <span className="text-muted-foreground text-xs">{t('results.firstScoring')}</span>
                )}
                <span className="text-muted-foreground ms-auto text-xs">{dateTime(r.finalizedAt)}</span>
              </div>
            ))}
          </CardContent>
        </Card>
      )}

      {data.registeredAt && (
        <p className="text-muted-foreground mt-4 text-xs">
          {t('candidate.registeredAt', { when: dateTime(data.registeredAt) })}
        </p>
      )}
    </div>
  );
}
